/**
 * Månadsvis besparingsgraf.
 * Visar hur mycket batteriet sparar per månad så användaren ser
 * tydligt under vilka delar av året batteriet faktiskt jobbar.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Cell,
} from "recharts";
import type { HourPoint, ResultHour, Scenario } from "../sim/types";
import { computeMonthlySavings } from "../sim/monthly-savings";

interface MonthlySavingsRow {
  monthKey: string;
  monthLabel: string;
  savingsSek: number;
}

const MONTH_NAMES_SV = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

function toChartRows(
  baselineHours: HourPoint[],
  batteryHours: ResultHour[],
  scenario: Scenario,
): MonthlySavingsRow[] {
  return computeMonthlySavings(baselineHours, batteryHours, scenario).map(
    (m) => ({
      monthKey: m.monthKey,
      monthLabel: MONTH_NAMES_SV[m.monthIndex] ?? "?",
      savingsSek: m.savingsSek,
    }),
  );
}

interface MonthlySavingsChartProps {
  baselineHours: HourPoint[];
  batteryHours: ResultHour[];
  scenario: Scenario;
}

interface TooltipPayload {
  payload: MonthlySavingsRow;
}

function SavingsTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  const v = Math.round(data.savingsSek);
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-month">{label}</div>
      <dl>
        <div>
          <dt>Besparing</dt>
          <dd className={v >= 0 ? "positive" : "negative"}>
            {v >= 0 ? "+" : ""}
            {new Intl.NumberFormat("sv-SE").format(v)} kr
          </dd>
        </div>
      </dl>
    </div>
  );
}

export default function MonthlySavingsChart({
  baselineHours,
  batteryHours,
  scenario,
}: MonthlySavingsChartProps) {
  const data = toChartRows(baselineHours, batteryHours, scenario);

  if (data.length === 0) return null;

  const total = data.reduce((s, r) => s + r.savingsSek, 0);
  const max = Math.max(...data.map((r) => Math.abs(r.savingsSek)));

  return (
    <div className="monthly-savings-chart">
      <header className="msc-header">
        <h3>Besparing per månad</h3>
        <span className="msc-total">
          {total >= 0 ? "+" : ""}
          {"\u00A0"}
          {new Intl.NumberFormat("sv-SE")
            .format(Math.round(total))
            .replace(/\s/g, "\u00A0")}
          {"\u00A0kr/år"}
        </span>
      </header>
      <p className="hint">
        Stapelhöjd = kr sparat den månaden. Negativa staplar betyder att
        batteriet kostar mer än det sparar (sällsynt men kan hända under
        höglast-vinter med skattereduktion på export).
      </p>

      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 16, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="var(--rule)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="monthLabel"
            stroke="var(--muted)"
            fontSize={12}
            tickLine={false}
            axisLine={{ stroke: "var(--rule)" }}
          />
          <YAxis
            stroke="var(--muted)"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `${Math.round(v)}`}
            domain={[-max * 1.1, max * 1.1]}
            label={{
              value: "kr",
              position: "insideTopLeft",
              fontSize: 11,
              fill: "var(--muted)",
            }}
          />
          <Tooltip
            cursor={{ fill: "var(--rule)", opacity: 0.3 }}
            content={<SavingsTooltip />}
          />
          <ReferenceLine y={0} stroke="var(--rule)" />
          <Bar dataKey="savingsSek" radius={[3, 3, 0, 0]}>
            {data.map((row, i) => (
              <Cell
                key={i}
                fill={
                  row.savingsSek >= 0
                    ? "var(--brand-green)"
                    : "#c02f2f"
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
